import { Marker, Popup } from 'react-leaflet';
import { LatLngTuple, DivIcon } from 'leaflet';
import { Workorder } from '@certhon/models';
import { DEFAULT_MARKER_BG } from '../utils/mapColors';

/**
 * Checkmark SVG for completed markers
 */
const CHECKMARK_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="4" stroke-linecap="round" stroke-linejoin="round" style="width:10px;height:10px;color:#fff;"><polyline points="20 6 9 17 4 12"></polyline></svg>`;

/**
 * Create a numbered icon for workorder markers
 */
function createNumberedIcon(
  number: number,
  backgroundColor: string = DEFAULT_MARKER_BG,
  completed: boolean = false,
): DivIcon {
  const completedBadge = completed
    ? `<div style="position:absolute;top:-4px;right:-4px;width:14px;height:14px;background:#10b981;border-radius:50%;display:flex;align-items:center;justify-content:center;border:2px solid #fff;">${CHECKMARK_SVG}</div>`
    : '';

  return new DivIcon({
    className: 'numbered-marker',
    html: `<div style="position:relative;display:flex;align-items:center;justify-content:center;width:24px;height:24px;background:${backgroundColor};color:#fff;border-radius:50%;font-size:12px;font-weight:600;box-shadow:0 2px 4px rgba(0,0,0,0.3);border:2px solid #fff;">${number}${completedBadge}</div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  });
}

/**
 * Create a colored icon without number (for multi-technician views)
 */
function createColoredIcon(color: string): DivIcon {
  return new DivIcon({
    className: 'colored-marker',
    html: `<div style="display:flex;align-items:center;justify-content:center;width:16px;height:16px;background:${color};border-radius:50%;box-shadow:0 2px 4px rgba(0,0,0,0.3);border:2px solid #fff;"></div>`,
    iconSize: [16, 16],
    iconAnchor: [8, 8],
  });
}

/**
 * Create a highlighted icon (larger with glow effect)
 */
function createHighlightedIcon(
  number?: number,
  backgroundColor: string = DEFAULT_MARKER_BG,
  completed: boolean = false,
): DivIcon {
  const content = number ? `${number}` : '';
  const completedBadge = completed
    ? `<div style="position:absolute;top:-4px;right:-4px;width:16px;height:16px;background:#10b981;border-radius:50%;display:flex;align-items:center;justify-content:center;border:2px solid #fff;">${CHECKMARK_SVG}</div>`
    : '';

  return new DivIcon({
    className: 'highlighted-marker',
    html: `<div style="position:relative;display:flex;align-items:center;justify-content:center;width:32px;height:32px;background:${backgroundColor};color:#fff;border-radius:50%;font-size:14px;font-weight:700;box-shadow:0 4px 8px rgba(0,0,0,0.4), 0 0 0 4px rgba(59,130,246,0.3);border:3px solid #fff;">${content}${completedBadge}</div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });
}

interface WorkorderMarkerProps {
  /**
   * Workorder to display
   */
  workorder: Workorder;
  /**
   * Position on the map [lat, lng]
   */
  position: LatLngTuple;
  /**
   * Index number to display on marker (1-based)
   */
  index?: number;
  /**
   * Custom color for the marker
   */
  color?: string;
  /**
   * Whether this marker is highlighted (hover state)
   */
  highlighted?: boolean;
  /**
   * Click handler
   */
  onClick?: () => void;
  /**
   * Whether to show popup by default
   */
  showPopup?: boolean;
}

/**
 * Workorder marker component for map displays
 * Supports numbered markers, color coding, and highlighting
 */
export function WorkorderMarker({
  workorder,
  position,
  index,
  color,
  highlighted = false,
  onClick,
  showPopup = false,
}: WorkorderMarkerProps) {
  // Determine icon based on props
  const icon = highlighted
    ? createHighlightedIcon(index, color, workorder.completed)
    : index !== undefined
      ? createNumberedIcon(index, color, workorder.completed)
      : color
        ? createColoredIcon(color)
        : createNumberedIcon(1, undefined, workorder.completed); // Fallback

  const eventHandlers = onClick
    ? {
        click: onClick,
      }
    : undefined;

  return (
    <Marker position={position} icon={icon} eventHandlers={eventHandlers}>
      <Popup>
        <strong>
          {index !== undefined ? `${index}. ` : ''}WO-{workorder.id}
        </strong>
        <br />
        {workorder.location_address || 'No address'}
        {workorder.customer_name && (
          <>
            <br />
            <span className="text-muted-foreground">
              {workorder.customer_name}
            </span>
          </>
        )}
      </Popup>
    </Marker>
  );
}
